// Percorso file: /frontend/src/pages/Assistant.jsx
//
// L'ASSISTENTE AI PER I SOCI (cantiere ASSISTENTE-SOCI).
//
// Il socio scrive una domanda su COME SI USA L'APP, in qualunque lingua, e la
// riceve indietro nella sua. La pagina non sa nulla del club: non legge dati,
// non compie azioni, non mostra anagrafiche. Manda una domanda scritta a
// `POST /assistant/ask` e mostra la risposta.
//
// ⚠️ STA DENTRO HomeShell, a differenza di Messaging che è pagina intera. Il
// telaio porta la barra utente e il «Back», e la colonna del §4: qui la si
// vuole, perché questa è una pagina normale del socio (disegno §6.2).
//
// ⚠️ NESSUN GATE DI RUOLO. La rotta sta dietro <PrivateRoute> nudo (App.jsx):
// dietro questa porta non c'è nulla di riservato — la guida è già potata della
// materia da funzionario e il system prompt rifiuta il resto (Decisione 3).
// Il gate unico dei ruoli resta `auth/access.js`: qui non se ne apre un secondo.
//
// ⚠️ L'INGRESSO È SEMPRE SCRITTO. La voce c'è solo in USCITA («Read aloud», la
// sintesi vocale del browser); il riconoscimento del parlato è fuori ambito.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeShell from "../components/HomeShell";
import AssistantMascot from "../components/AssistantMascot";
import { api, TIMEOUT_LUNGO_MS } from "../api/api";
import "./Assistant.css";

// I turni che si rimandano indietro come contesto, perché un «and then?» abbia
// senso. Pochi di proposito: il blocco pesante è la guida, che viaggia in cache
// lato server, e allungare lo storico non aggiunge nulla se non spesa.
const TURNI_DI_CONTESTO = 6;

// I DUE TAG in coda a ogni risposta, ciascuno su una riga sua:
//   [MOOD: happy|confused|talking]   — l'umore, per la mascotte
//   [LANG: xx]                       — la lingua della risposta, per la voce
// Li estrae il BROWSER e li TOGLIE dal testo mostrato — al socio non devono mai
// comparire. Se mancano o sono malformati, si mostra il testo intero, l'umore
// resta quello di prima e la voce ripiega sull'inglese: un tag storto non deve
// rompere la pagina.
//
// ⚠️ Le due regex sono ancorate a FINE testo e si applicano a turno, finché
// tolgono qualcosa: così l'ordine in cui il modello scrive i due tag non conta
// (il prompt chiede MOOD e poi LANG, ma un modello che li inverte non rompe
// nulla). Il codice lingua ammesso è ISO 639-1: due lettere, più un eventuale
// suffisso di regione (`pt-BR`, `zh-TW`) che la sintesi vocale capisce da sé.
// ⚠️ La regex della lingua TOGLIE il tag qualunque cosa contenga, e il codice
// si valida a parte: un `[LANG: english]` storto non deve né comparire a
// schermo né fare da tappo al `[MOOD]` che gli sta sopra.
const RIGA_UMORE = /\s*\[MOOD:\s*([a-zA-Z]+)\s*\]\s*$/;
const RIGA_LINGUA = /\s*\[LANG:\s*([^\]]*)\]\s*$/;
const CODICE_LINGUA = /^[a-z]{2}(?:-[a-z]{2,4})?$/;
const UMORI_DAL_MODELLO = ["happy", "confused", "talking"];

function separaTag(testoGrezzo) {
  let testo = String(testoGrezzo || "");
  let umore = null;
  let lingua = null;

  // Al massimo due giri: un tag per tipo. Il terzo giro non troverebbe nulla.
  for (let giro = 0; giro < 2; giro++) {
    const trovatoLingua = testo.match(RIGA_LINGUA);
    if (trovatoLingua) {
      const codice = String(trovatoLingua[1]).trim().toLowerCase();
      // Un codice che non ha la forma giusta si scarta: la voce ripiegherà.
      lingua = CODICE_LINGUA.test(codice) ? codice : null;
      testo = testo.replace(RIGA_LINGUA, "");
      continue;
    }
    const trovatoUmore = testo.match(RIGA_UMORE);
    if (trovatoUmore) {
      umore = String(trovatoUmore[1]).toLowerCase();
      testo = testo.replace(RIGA_UMORE, "");
      continue;
    }
    break;
  }

  return {
    testo: testo.trim(),
    // Un valore che non conosciamo non si passa alla mascotte: si ignora.
    // (Il componente ha comunque il suo ripiego su `idle`.)
    umore: UMORI_DAL_MODELLO.includes(umore) ? umore : null,
    lingua
  };
}

// LA LINGUA DI RIPIEGO DELLA VOCE: l'inglese, la lingua base dell'app. «Nel
// dubbio, inglese» — e mai la voce del telefono (che può essere italiana) a
// leggere una lingua che non è la sua.
const LINGUA_DI_RIPIEGO = "en";

// IL TESTO PER LA VOCE, non quello per lo schermo. La bolla resta com'è (emoji
// e grassetto a schermo fanno simpatia); alla sintesi vocale, che altrimenti
// legge «asterisco asterisco», arrivano solo le parole:
//   - via i segni del markdown (**grassetto**, *corsivo*, _corsivo_, `codice`,
//     # titoli, > citazioni, i trattini e gli asterischi degli elenchi);
//   - via emoji e simboli (Unicode `\p{Extended_Pictographic}`, i selettori di
//     variante e gli zero-width joiner che li compongono, e i simboli `\p{S}`,
//     che la lettera e la cifra non sono);
//   - tenute le lettere di ogni alfabeto (`\p{L}`), le cifre, la punteggiatura
//     ordinaria (che dà il respiro alla lettura) e gli spazi.
function testoPerLaVoce(testo) {
  return String(testo || "")
    // Righe di soli separatori (--- / ***) e i marcatori di titolo/citazione.
    .replace(/^[ \t]*([-*_]){3,}[ \t]*$/gm, "")
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]*>[ \t]?/gm, "")
    // I puntini degli elenchi: «- passo» / «* passo» / «+ passo» → «passo».
    .replace(/^[ \t]*[-*+][ \t]+/gm, "")
    // Grassetto, corsivo, codice: si toglie il segno, si tiene il contenuto.
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/`+([^`]*)`+/g, "$1")
    // Emoji e simboli, coi pezzi invisibili che li compongono.
    .replace(/[\p{Extended_Pictographic}\p{S}\uFE0E\uFE0F\u200D]/gu, " ")
    // Ciò che di markdown fosse rimasto sciolto.
    .replace(/[*_#`~\\[\]]/g, " ")
    // Gli spazi che tutto questo ha lasciato.
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+([,.;:!?])/g, "$1")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// LA SCELTA DELLA VOCE per una lingua. Regola del committente: la voce
// madrelingua della lingua della risposta; per l'inglese una qualunque voce
// inglese (nessuna preferenza fra UK e US); se il telefono non ha una voce per
// quella lingua, si ripiega sull'inglese. Se non ha nemmeno quella, si lascia
// `voice` vuoto ma `lang` impostato: il motore sceglie lui, ma sa che lingua è.
//
// Il confronto è sul PREFISSO di lingua (`en` ⊂ `en-AU`, `en_GB`), perché i
// dispositivi scrivono il codice con il trattino o con il trattino basso.
function stessaLingua(codiceVoce, lingua) {
  const base = String(codiceVoce || "").toLowerCase().replace("_", "-");
  return base === lingua || base.startsWith(lingua + "-");
}

function scegliVoce(voci, lingua) {
  let candidate = voci.filter((v) => stessaLingua(v.lang, lingua));
  // `pt-br` su un telefono che ha solo `pt-PT`: meglio il portoghese che
  // l'inglese. Si riprova con le sole due lettere della lingua.
  if (candidate.length === 0 && lingua.length > 2) {
    candidate = voci.filter((v) => stessaLingua(v.lang, lingua.slice(0, 2)));
  }
  if (candidate.length === 0) return null;
  // Fra le voci della lingua, quella che il dispositivo segna come default;
  // altrimenti la prima: è la «voce generica» che il committente vuole.
  return candidate.find((v) => v.default) || candidate[0];
}

// LA TRAPPOLA DELLE VOCI: `getVoices()` su alcuni browser (Chrome in testa) è
// VUOTO finché non scatta `voiceschanged`, che arriva una volta sola e presto.
// Si prova subito; se non c'è nulla, si aspetta l'evento — con un tetto di
// tempo, perché su qualche dispositivo l'evento non arriva mai e la lettura
// deve partire lo stesso (con la sola `lang`, senza voce scelta).
function elencoVoci() {
  return new Promise((risolvi) => {
    const subito = window.speechSynthesis.getVoices();
    if (subito && subito.length > 0) return risolvi(subito);

    let chiuso = false;
    const consegna = () => {
      if (chiuso) return;
      chiuso = true;
      window.speechSynthesis.removeEventListener("voiceschanged", consegna);
      risolvi(window.speechSynthesis.getVoices() || []);
    };
    window.speechSynthesis.addEventListener("voiceschanged", consegna);
    setTimeout(consegna, 1000);
  });
}

export default function Assistant() {
  const navigate = useNavigate();

  // Le bolle a schermo. `ruolo` è "user" o "assistant" — gli stessi nomi che
  // vuole il backend, così lo storico si ricava da qui senza tradurre nulla.
  const [bolle, setBolle] = useState([]);
  const [testoScritto, setTestoScritto] = useState("");
  const [inAttesa, setInAttesa] = useState(false);
  const [umore, setUmore] = useState("idle");
  // QUALE bolla si sta leggendo, non «se» si sta leggendo: con un booleano solo,
  // il bottone di OGNI risposta cambierebbe insieme, e premerlo sulla terza
  // fermerebbe la lettura della prima invece di leggerla.
  const [indiceLetto, setIndiceLetto] = useState(null);

  const fondoRef = useRef(null);
  // Il numero dell'ULTIMA lettura chiesta. `leggi` aspetta le voci del
  // dispositivo (v. `elencoVoci`) e, tornata dall'attesa, parte solo se nel
  // frattempo nessuno ha chiesto altro: ogni «Stop», ogni nuova domanda e ogni
  // altra lettura avanzano il contatore. Un ref e non uno stato: non deve
  // ridisegnare nulla.
  const contatoreLettureRef = useRef(0);

  // La sintesi vocale è del browser: su qualcuno può non esserci.
  const vocePossibile =
    typeof window !== "undefined" && "speechSynthesis" in window;

  // Lo scorrimento segue l'ultima bolla.
  useEffect(() => {
    if (fondoRef.current) {
      fondoRef.current.scrollIntoView({ block: "end" });
    }
  }, [bolle, inAttesa]);

  // Uscendo dalla pagina la lettura si ferma: una voce che continua a parlare
  // su un'altra schermata è un difetto, non una funzione.
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  async function invia(evento) {
    if (evento) evento.preventDefault();

    const domanda = testoScritto.trim();
    if (!domanda || inAttesa) return;

    // Se stava leggendo (o aspettava le voci per farlo), la nuova domanda ha la
    // precedenza.
    if (vocePossibile) window.speechSynthesis.cancel();
    contatoreLettureRef.current++;
    setIndiceLetto(null);

    // LO STORICO si prende PRIMA di aggiungere la domanda nuova: quella viaggia
    // per conto suo, nel campo `question`.
    const storico = bolle
      .filter((b) => !b.errore)
      .slice(-TURNI_DI_CONTESTO)
      .map((b) => ({ role: b.ruolo, content: b.testo }));

    setBolle((precedenti) => [
      ...precedenti,
      { ruolo: "user", testo: domanda }
    ]);
    setTestoScritto("");
    setInAttesa(true);
    // L'attesa la decide il FRONTEND, non il modello: è il tempo della chiamata.
    setUmore("thinking");

    try {
      const risposta = await api.post("/assistant/ask", {
        question: domanda,
        history: storico
      }, { timeoutMs: TIMEOUT_LUNGO_MS });

      const { testo, umore: umoreDetto, lingua } = separaTag(
        risposta && risposta.answer
      );

      setBolle((precedenti) => [
        ...precedenti,
        {
          ruolo: "assistant",
          testo: testo || "I couldn't put together an answer. Please try again.",
          // La lingua dichiarata dal modello, per la voce. Se manca, `leggi`
          // ripiega da sé sull'inglese: qui non si decide.
          lingua: lingua || null
        }
      ]);
      setUmore(umoreDetto || "happy");
    } catch (err) {
      // `api.js` distingue i due guasti: una risposta HTTP non-ok porta con sé
      // `err.status`, un guasto di rete no. Non si ritenta alla cieca — si dice
      // che cosa è successo, con parole gentili.
      let messaggio;
      if (err && err.status === 503) {
        messaggio =
          "The assistant isn't switched on yet. Please contact the club at footballdepartment@queensparksc.com.au";
      } else if (err && err.status) {
        messaggio = "The assistant couldn't answer just now. Please try again.";
      } else {
        messaggio =
          "I can't reach the assistant right now. Please check your connection and try again.";
      }

      setBolle((precedenti) => [
        ...precedenti,
        { ruolo: "assistant", testo: messaggio, errore: true }
      ]);
      setUmore("confused");
    } finally {
      setInAttesa(false);
    }
  }

  // «Leggi ad alta voce». Mentre legge, la mascotte parla; a fine lettura torna
  // contenta. Anche qui l'umore lo decide il frontend, non il modello.
  //
  // LA LINGUA E LA VOCE: si legge nella lingua che il modello ha dichiarato col
  // tag [LANG], con una voce madrelingua se il dispositivo ce l'ha; altrimenti
  // in inglese. Senza questo, la voce è quella di default del telefono — un
  // telefono in italiano leggeva l'inglese con la voce italiana.
  // IL TESTO: quello ripulito da `testoPerLaVoce`, non quello della bolla.
  //
  // È `async` per la trappola delle voci (v. `elencoVoci`): tra il tocco e
  // l'inizio della lettura può passare fino a un secondo, su un dispositivo che
  // non ha ancora caricato le voci; poi restano in memoria e non si aspetta più.
  async function leggi(testo, lingua, indice) {
    if (!vocePossibile) return;

    window.speechSynthesis.cancel();
    const numeroLettura = ++contatoreLettureRef.current;

    const daLeggere = testoPerLaVoce(testo);
    if (!daLeggere) return;

    // Lingua della risposta, o inglese se il tag manca (`separaTag` ha già
    // scartato i valori malformati).
    const linguaChiesta = lingua || LINGUA_DI_RIPIEGO;

    const voci = await elencoVoci();
    let voce = scegliVoce(voci, linguaChiesta);
    let linguaLetta = linguaChiesta;
    if (!voce && linguaChiesta !== LINGUA_DI_RIPIEGO) {
      // Il dispositivo non parla quella lingua: inglese, mai la voce di sistema.
      voce = scegliVoce(voci, LINGUA_DI_RIPIEGO);
      linguaLetta = LINGUA_DI_RIPIEGO;
    }

    // Mentre si aspettavano le voci, il socio può aver toccato «Stop», mandato
    // un'altra domanda o chiesto un'altra lettura: ogni gesto di quelli avanza
    // il contatore, e questa lettura, ormai vecchia, non parte.
    if (numeroLettura !== contatoreLettureRef.current) return;

    const lettura = new window.SpeechSynthesisUtterance(daLeggere);
    // `lang` sempre: se non si è trovata una voce, il motore la sceglie lui, ma
    // sa in che lingua deve leggere. La voce, se c'è, la si passa esplicita.
    lettura.lang = voce ? voce.lang : linguaLetta;
    if (voce) lettura.voice = voce;
    lettura.onstart = () => {
      setIndiceLetto(indice);
      setUmore("talking");
    };
    lettura.onend = () => {
      setIndiceLetto(null);
      setUmore("happy");
    };
    lettura.onerror = () => {
      setIndiceLetto(null);
      setUmore("idle");
    };

    window.speechSynthesis.speak(lettura);
  }

  function fermaLettura() {
    if (!vocePossibile) return;
    window.speechSynthesis.cancel();
    contatoreLettureRef.current++;
    setIndiceLetto(null);
    setUmore("happy");
  }

  // Invio manda, Maiusc+Invio va a capo: è il modo della Live Chat, e il socio
  // che conosce l'una si trova a casa nell'altra.
  function tastiera(evento) {
    if (evento.key === "Enter" && !evento.shiftKey) {
      evento.preventDefault();
      invia();
    }
  }

  return (
    <HomeShell backTo={() => navigate(-1)} showSwitch={false}>
      <div className="asst__page">
        {/* Grande all'apertura, più piccola appena parte la conversazione: la
            larghezza (70% / 50% dello schermo, con tetto per desktop) la porta
            il CONTENITORE in Assistant.css; la mascotte la riempie al 100%. */}
        <div
          className={
            "asst__mascot" + (bolle.length > 0 ? " asst__mascot--chat" : "")
          }
        >
          {/* ⚠️ `title` in INGLESE e sempre passata: finisce in `aria-label`,
              cioè è testo che l'utente RICEVE (lettore di schermo), e il
              default interno del componente è in italiano. Passandola qui la
              si copre senza toccare il componente. */}
          <AssistantMascot mood={umore} size="100%" title="QPSC Assistant" />
        </div>

        <p className="asst__intro">
          Ask me how to use the app — in your own language.
        </p>

        <div className="asst__thread">
          {bolle.map((bolla, indice) => {
            const mia = bolla.ruolo === "user";
            return (
              <React.Fragment key={indice}>
                <div
                  className={
                    "asst__bubble " +
                    (mia ? "asst__bubble--me" : "asst__bubble--bot") +
                    (bolla.errore ? " asst__bubble--error" : "")
                  }
                  /* ⚠️ `dir="auto"` sulla BOLLA, non sull'app: una risposta in
                     arabo va letta da destra a sinistra, ma solo lì dentro. */
                  dir="auto"
                >
                  {bolla.testo}
                </div>

                {/* «Read aloud» solo sulle risposte vere, non sui ripieghi. */}
                {!mia && !bolla.errore && vocePossibile && (
                  <button
                    type="button"
                    className="asst__speak"
                    onClick={() =>
                      indiceLetto === indice
                        ? fermaLettura()
                        : leggi(bolla.testo, bolla.lingua, indice)
                    }
                  >
                    {indiceLetto === indice ? "Stop reading" : "Read aloud"}
                  </button>
                )}
              </React.Fragment>
            );
          })}

          {inAttesa && <div className="asst__waiting">Thinking…</div>}

          <div ref={fondoRef} />
        </div>

        <form className="asst__composer" onSubmit={invia}>
          <textarea
            className="asst__input"
            value={testoScritto}
            onChange={(e) => setTestoScritto(e.target.value)}
            onKeyDown={tastiera}
            placeholder="Ask a question…"
            rows={1}
            dir="auto"
          />
          <button
            type="submit"
            className="qpsc-btn-gold asst__send"
            disabled={inAttesa || !testoScritto.trim()}
          >
            Send
          </button>
        </form>
      </div>
    </HomeShell>
  );
}
